import { promises } from 'fs';
import { Readable, Writable } from 'stream';
import * as http from 'http';
import * as https from 'https';
import { join, normalize, resolve, dirname } from 'path';
import sharp2 from 'sharp';
import * as ort from 'onnxruntime-node';
import { createHash } from 'crypto';
import { tmpdir } from 'os';

// src/io/input.ts
var BLOCKED_HOSTS = [
  "localhost",
  "127.0.0.1",
  "0.0.0.0",
  "169.254.169.254",
  // AWS metadata
  "::1",
  "metadata.google.internal"
  // GCP metadata
];
var ALLOWED_CONTENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/jpg"
];
async function handleInput(input) {
  if (Buffer.isBuffer(input)) {
    return input;
  }
  if (input instanceof Readable) {
    return streamToBuffer(input);
  }
  if (typeof input === "string") {
    if (input.startsWith("http://") || input.startsWith("https://")) {
      return downloadFromUrl(input);
    }
    return readFromFile(input);
  }
  throw new Error(`Invalid input type: ${typeof input}`);
}
function validateFilePath(filePath, baseDir) {
  const normalizedPath = normalize(resolve(filePath));
  const sensitivePatterns = [
    "/etc/",
    "/proc/",
    "/sys/",
    "/dev/",
    "/boot/",
    "C:\\Windows\\",
    "C:\\Program Files\\"
  ];
  for (const pattern of sensitivePatterns) {
    if (normalizedPath.includes(pattern)) {
      throw new Error(`Access denied: Cannot read from system directory "${pattern}"`);
    }
  }
  return normalizedPath;
}
async function readFromFile(filePath) {
  try {
    const validatedPath = validateFilePath(filePath);
    await promises.access(validatedPath, promises.constants.R_OK);
    return await promises.readFile(validatedPath);
  } catch (error) {
    throw new Error(
      `Failed to read file "${filePath}": ${error instanceof Error ? error.message : String(error)}`
    );
  }
}
function validateUrl(urlString) {
  let parsedUrl;
  try {
    parsedUrl = new URL(urlString);
  } catch {
    throw new Error(`Invalid URL: "${urlString}"`);
  }
  if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
    throw new Error(
      `Invalid URL protocol: "${parsedUrl.protocol}". Only HTTP and HTTPS are allowed.`
    );
  }
  const hostname = parsedUrl.hostname.toLowerCase();
  if (BLOCKED_HOSTS.includes(hostname)) {
    throw new Error(`Access denied: Cannot download from blocked host "${hostname}"`);
  }
  if (hostname.startsWith("10.") || hostname.startsWith("192.168.") || hostname.match(/^172\.(1[6-9]|2[0-9]|3[01])\./)) {
    throw new Error(`Access denied: Cannot download from private IP address "${hostname}"`);
  }
  return parsedUrl;
}
async function downloadFromUrl(url, maxRedirects = 3) {
  const parsedUrl = validateUrl(url);
  return new Promise((resolve4, reject) => {
    const protocol = parsedUrl.protocol === "https:" ? https : http;
    protocol.get(url, (response) => {
      const contentType = response.headers["content-type"]?.toLowerCase() || "";
      const isValidContentType = ALLOWED_CONTENT_TYPES.some(
        (type) => contentType.includes(type)
      );
      if (!isValidContentType) {
        reject(
          new Error(
            `Invalid content type: "${contentType}". Expected image/* content type.`
          )
        );
        return;
      }
      if (response.statusCode === 301 || response.statusCode === 302 || response.statusCode === 307 || response.statusCode === 308) {
        if (maxRedirects <= 0) {
          reject(new Error("Too many redirects"));
          return;
        }
        const redirectUrl = response.headers.location;
        if (!redirectUrl) {
          reject(new Error("Redirect location not found"));
          return;
        }
        try {
          validateUrl(redirectUrl);
        } catch (error) {
          reject(error);
          return;
        }
        downloadFromUrl(redirectUrl, maxRedirects - 1).then(resolve4).catch(reject);
        return;
      }
      if (!response.statusCode || response.statusCode < 200 || response.statusCode >= 300) {
        reject(new Error(`HTTP ${response.statusCode}: Failed to download image from ${url}`));
        return;
      }
      const chunks = [];
      response.on("data", (chunk) => chunks.push(chunk));
      response.on("end", () => resolve4(Buffer.concat(chunks)));
      response.on("error", reject);
    }).on("error", (error) => {
      reject(new Error(`Failed to download image from "${url}": ${error.message}`));
    });
  });
}
async function streamToBuffer(stream) {
  return new Promise((resolve4, reject) => {
    const chunks = [];
    stream.on("data", (chunk) => chunks.push(chunk));
    stream.on("end", () => resolve4(Buffer.concat(chunks)));
    stream.on("error", reject);
  });
}
async function validateInput(buffer) {
  if (buffer.length === 0) {
    throw new Error("Image buffer is empty");
  }
  const MAX_FILE_SIZE = 50 * 1024 * 1024;
  if (buffer.length > MAX_FILE_SIZE) {
    throw new Error(
      `Image file too large: ${(buffer.length / 1024 / 1024).toFixed(2)}MB (max: ${MAX_FILE_SIZE / 1024 / 1024}MB)`
    );
  }
  try {
    const metadata = await sharp2(buffer).metadata();
    const supportedFormats = ["jpeg", "png", "webp", "jpg"];
    if (!metadata.format || !supportedFormats.includes(metadata.format)) {
      throw new Error(
        `Unsupported image format: "${metadata.format}". Supported formats: ${supportedFormats.join(", ")}`
      );
    }
    if (!metadata.width || !metadata.height) {
      throw new Error("Invalid image: Unable to determine dimensions");
    }
    if (metadata.width < 1 || metadata.height < 1) {
      throw new Error(
        `Invalid image dimensions: ${metadata.width}x${metadata.height}`
      );
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes("Unsupported image format")) {
      throw error;
    }
    throw new Error(
      `Invalid image file: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}
async function handleOutput(buffer, output) {
  if (!output) {
    return buffer;
  }
  if (typeof output === "string") {
    await writeToFile(buffer, output);
    return buffer;
  }
  if (output instanceof Writable) {
    await writeToStream(buffer, output);
    return buffer;
  }
  throw new Error(`Invalid output type: ${typeof output}`);
}
function validateOutputPath(filePath, baseDir) {
  const normalizedPath = normalize(resolve(filePath));
  const sensitivePatterns = [
    "/etc/",
    "/proc/",
    "/sys/",
    "/dev/",
    "/boot/",
    "/bin/",
    "/sbin/",
    "/usr/bin/",
    "/usr/sbin/",
    "C:\\Windows\\",
    "C:\\Program Files\\",
    "C:\\Program Files (x86)\\"
  ];
  for (const pattern of sensitivePatterns) {
    if (normalizedPath.includes(pattern)) {
      throw new Error(`Access denied: Cannot write to system directory "${pattern}"`);
    }
  }
  return normalizedPath;
}
async function writeToFile(buffer, filePath) {
  try {
    const validatedPath = validateOutputPath(filePath);
    const dir = dirname(validatedPath);
    await promises.mkdir(dir, { recursive: true });
    try {
      await promises.access(dir, promises.constants.W_OK);
    } catch {
      throw new Error(`No write permission for directory "${dir}"`);
    }
    await promises.writeFile(validatedPath, buffer);
  } catch (error) {
    throw new Error(
      `Failed to write file "${filePath}": ${error instanceof Error ? error.message : String(error)}`
    );
  }
}
async function writeToStream(buffer, stream) {
  return new Promise((resolve4, reject) => {
    let resolved = false;
    const onError = (error) => {
      if (!resolved) {
        resolved = true;
        reject(new Error(`Failed to write to stream: ${error.message}`));
      }
    };
    const onFinish = () => {
      if (!resolved) {
        resolved = true;
        cleanup();
        resolve4();
      }
    };
    const cleanup = () => {
      stream.removeListener("error", onError);
      stream.removeListener("finish", onFinish);
    };
    stream.once("error", onError);
    stream.once("finish", onFinish);
    const canContinue = stream.write(buffer);
    if (!canContinue) {
      stream.once("drain", () => {
        stream.end();
      });
    } else {
      stream.end();
    }
  });
}

// src/core/types.ts
var defaultMaxResolution = 2048;
async function downloadFile(url, onProgress) {
  return new Promise((resolve4, reject) => {
    const client = url.startsWith("https") ? https : http;
    client.get(url, (response) => {
      if (response.statusCode === 302 || response.statusCode === 301) {
        if (response.headers.location) {
          return downloadFile(response.headers.location, onProgress).then(resolve4).catch(reject);
        }
      }
      if (response.statusCode !== 200) {
        reject(new Error(`Failed to download: ${response.statusCode}`));
        return;
      }
      const chunks = [];
      let receivedLength = 0;
      response.on("data", (chunk) => {
        chunks.push(chunk);
        receivedLength += chunk.length;
        onProgress?.(receivedLength);
      });
      response.on("end", () => {
        resolve4(Buffer.concat(chunks));
      });
      response.on("error", reject);
    }).on("error", reject);
  });
}
async function loadModel(model, options) {
  const { onProgress, cache, enableCache = true } = options ?? {};
  if (enableCache && cache) {
    const cacheKey = model.publicPath + model.files.join(",");
    const cached = await cache.get(cacheKey);
    if (cached) {
      onProgress?.(1);
      return cached;
    }
  }
  const loaded = Array(model.files.length);
  const chunks = await Promise.all(
    model.files.map(async (file, index) => {
      loaded[index] = 0;
      const url = model.publicPath + file;
      return downloadFile(url, (length) => {
        loaded[index] = length;
        onProgress?.(loaded.reduce((acc, item) => acc + item, 0) / model.size);
      });
    })
  );
  const data = Buffer.concat(chunks);
  if (data.length !== model.size) {
    throw new Error(
      `Failed to fetch ${model.name} with size ${model.size} but got ${data.length}`
    );
  }
  if (enableCache && cache) {
    const cacheKey = model.publicPath + model.files.join(",");
    await cache.set(cacheKey, data).catch(() => {
    });
  }
  return data;
}

// src/core/utils.ts
function calculateProportionalSize(originalWidth, originalHeight, maxWidth, maxHeight) {
  if (originalWidth > maxWidth || originalHeight > maxHeight) {
    const widthRatio = maxWidth / originalWidth;
    const heightRatio = maxHeight / originalHeight;
    const scalingFactor = Math.min(widthRatio, heightRatio);
    const newWidth = Math.floor(originalWidth * scalingFactor);
    const newHeight = Math.floor(originalHeight * scalingFactor);
    return [newWidth, newHeight];
  }
  return [originalWidth, originalHeight];
}
function imageDataToFloat32Array(data, width, height, mean = [128, 128, 128], std = [256, 256, 256]) {
  const stride = width * height;
  const float32Data = new Float32Array(3 * stride);
  for (let i = 0, j = 0; i < data.length; i += 4, j += 1) {
    float32Data[j] = (data[i] - mean[0]) / std[0];
    float32Data[j + stride] = (data[i + 1] - mean[1]) / std[1];
    float32Data[j + stride + stride] = (data[i + 2] - mean[2]) / std[2];
  }
  return float32Data;
}

// src/core/models.ts
function createBriaaiModel(publicPath = "https://unpkg.com/@rmbg/model-briaai@0.0.1/") {
  return {
    name: "briaai",
    files: [
      "briaai-1.onnx",
      "briaai-2.onnx",
      "briaai-3.onnx",
      "briaai-4.onnx",
      "briaai-5.onnx"
    ],
    mime: "application/octet-stream",
    publicPath,
    resolution: 1024,
    size: 44403226
  };
}
function createModnetModel(publicPath = "https://unpkg.com/@rmbg/model-modnet@0.0.1/") {
  return {
    name: "modnet",
    files: ["modnet-1.onnx", "modnet-2.onnx", "modnet-3.onnx"],
    mime: "application/octet-stream",
    publicPath,
    resolution: 512,
    size: 25888640
  };
}
function createU2netpModel(publicPath = "https://unpkg.com/@rmbg/model-u2netp@0.0.1/") {
  return {
    name: "u2netp",
    files: ["u2netp.onnx"],
    mime: "application/octet-stream",
    publicPath,
    resolution: 320,
    size: 4574861
  };
}

// src/core/index.ts
async function rmbgCore(inputBuffer, model, options = {}) {
  const { maxResolution = defaultMaxResolution, onProgress, cache, enableCache = true } = options;
  let session = null;
  try {
    let progress = 0;
    const modelData = await loadModel(model, {
      onProgress(value) {
        onProgress?.(progress + 1 / 2 * value, progress + 1 / 2 * value, 0);
      },
      cache,
      enableCache
    });
    session = await ort.InferenceSession.create(modelData, {
      executionProviders: ["cpu"],
      graphOptimizationLevel: "all",
      enableCpuMemArena: true
    }).catch((e) => {
      throw new Error(`Failed to create session: ${e.message}`);
    });
    progress += 1 / 2;
    const imageBuffer = await sharp2(inputBuffer).raw().ensureAlpha().toBuffer({
      resolveWithObject: true
    });
    const { data: originalData, info } = imageBuffer;
    const { width: originalWidth, height: originalHeight } = info;
    const tensorImageBuffer = await sharp2(originalData, {
      raw: { width: originalWidth, height: originalHeight, channels: 4 }
    }).resize(model.resolution, model.resolution, {
      fit: "fill",
      // Browser uses fill to match exact model input size
      kernel: "lanczos3"
    }).raw().toBuffer({ resolveWithObject: true });
    const tensorImageData = new Uint8ClampedArray(tensorImageBuffer.data);
    const inputTensor = imageDataToFloat32Array(
      tensorImageData,
      model.resolution,
      model.resolution
    );
    const outputData = await session.run({
      [session.inputNames[0]]: new ort.Tensor("float32", inputTensor, [
        1,
        3,
        model.resolution,
        model.resolution
      ])
    });
    const { outputNames } = session;
    const output = outputData[outputNames[0]];
    const maskData = output.data;
    const stride = model.resolution * model.resolution;
    for (let i = 0; i < stride; i++) {
      const alpha = maskData[i];
      tensorImageData[i * 4 + 3] = Math.round(alpha * 255);
    }
    const resizedTensorImage = await sharp2(Buffer.from(tensorImageData), {
      raw: { width: model.resolution, height: model.resolution, channels: 4 }
    }).resize(originalWidth, originalHeight, {
      fit: "fill",
      kernel: "lanczos3"
    }).raw().toBuffer();
    const outputBuffer = Buffer.from(originalData);
    for (let i = 0; i < originalWidth * originalHeight; i++) {
      const alpha = resizedTensorImage[i * 4 + 3];
      outputBuffer[i * 4 + 3] = alpha;
      if (alpha === 0) {
        outputBuffer[i * 4] = 0;
        outputBuffer[i * 4 + 1] = 0;
        outputBuffer[i * 4 + 2] = 0;
      }
    }
    const [finalWidth, finalHeight] = calculateProportionalSize(
      originalWidth,
      originalHeight,
      maxResolution,
      maxResolution
    );
    let result = sharp2(outputBuffer, {
      raw: { width: originalWidth, height: originalHeight, channels: 4 }
    });
    if (finalWidth !== originalWidth || finalHeight !== originalHeight) {
      result = result.resize(finalWidth, finalHeight, {
        kernel: "lanczos3"
      });
    }
    onProgress?.(1, 1, 1);
    return await result.png().toBuffer();
  } catch (error) {
    throw new Error(
      `Background removal failed: ${error instanceof Error ? error.message : String(error)}`
    );
  } finally {
    if (session) {
      try {
        await session.release();
      } catch (releaseError) {
        console.warn("Failed to release ONNX session:", releaseError);
      }
    }
  }
}
function validateCacheDir(cacheDir) {
  const normalizedPath = normalize(resolve(cacheDir));
  const blockedPatterns = [
    "/etc/",
    "/proc/",
    "/sys/",
    "/dev/",
    "/boot/",
    "/bin/",
    "/sbin/",
    "C:\\Windows\\",
    "C:\\Program Files\\"
  ];
  for (const pattern of blockedPatterns) {
    if (normalizedPath.includes(pattern)) {
      throw new Error(
        `Invalid cache directory: Cannot use system directory "${pattern}"`
      );
    }
  }
  const systemTmpDir = normalize(resolve(tmpdir()));
  const isInTmpDir = normalizedPath.startsWith(systemTmpDir);
  if (!isInTmpDir) {
    const userHome = process.env.HOME || process.env.USERPROFILE || "";
    const isInUserDir = userHome && normalizedPath.startsWith(normalize(resolve(userHome)));
    if (!isInUserDir) {
      console.warn(
        `Warning: Cache directory "${normalizedPath}" is not in temp or user directory. Using system tmpdir instead.`
      );
      return join(systemTmpDir, "rmbg-models");
    }
  }
  return normalizedPath;
}
var ModelCache = class {
  constructor(cacheDir) {
    this.cacheDir = validateCacheDir(cacheDir);
  }
  /**
   * Get cache key for model file
   */
  getCacheKey(url) {
    return createHash("md5").update(url).digest("hex");
  }
  /**
   * Get cache file path
   */
  getCachePath(url) {
    const key = this.getCacheKey(url);
    return join(this.cacheDir, `${key}.onnx`);
  }
  /**
   * Check if model is cached
   */
  async has(url) {
    const path = this.getCachePath(url);
    try {
      await promises.access(path);
      return true;
    } catch {
      return false;
    }
  }
  /**
   * Get cached model
   */
  async get(url) {
    const path = this.getCachePath(url);
    try {
      return await promises.readFile(path);
    } catch {
      return null;
    }
  }
  /**
   * Save model to cache
   */
  async set(url, data) {
    const path = this.getCachePath(url);
    await promises.mkdir(this.cacheDir, { recursive: true });
    await promises.writeFile(path, data);
  }
  /**
   * Clear cache
   */
  async clear() {
    try {
      await promises.rm(this.cacheDir, { recursive: true, force: true });
    } catch {
    }
  }
  /**
   * Get cache size in bytes
   */
  async getSize() {
    try {
      const files = await promises.readdir(this.cacheDir);
      let totalSize = 0;
      for (const file of files) {
        const stats = await promises.stat(join(this.cacheDir, file));
        totalSize += stats.size;
      }
      return totalSize;
    } catch {
      return 0;
    }
  }
};
var DEFAULT_CONFIG = {
  /** Default model to use */
  model: createU2netpModel(),
  /** Default maximum resolution */
  maxResolution: 2048,
  /** Default cache directory */
  cacheDir: join(tmpdir(), "rmbg-models"),
  /** Enable caching by default */
  enableCache: true
};
function getConfigFromEnv() {
  return {
    cacheDir: process.env.RMBG_CACHE_DIR || DEFAULT_CONFIG.cacheDir,
    enableCache: process.env.RMBG_ENABLE_CACHE !== "false"
  };
}

// src/index.ts
async function rmbg(input, options = {}) {
  try {
    const envConfig = getConfigFromEnv();
    const {
      model = DEFAULT_CONFIG.model,
      maxResolution = DEFAULT_CONFIG.maxResolution,
      output,
      onProgress,
      abortController,
      cacheDir = envConfig.cacheDir,
      enableCache = envConfig.enableCache
    } = options;
    const cache = enableCache ? new ModelCache(cacheDir) : void 0;
    if (abortController?.signal.aborted) {
      throw new Error("Operation aborted");
    }
    const inputBuffer = await handleInput(input);
    await validateInput(inputBuffer);
    if (abortController?.signal.aborted) {
      throw new Error("Operation aborted");
    }
    const resultBuffer = await rmbgCore(inputBuffer, model, {
      maxResolution,
      onProgress,
      cache,
      enableCache
    });
    if (abortController?.signal.aborted) {
      throw new Error("Operation aborted");
    }
    return await handleOutput(resultBuffer, output);
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(`Background removal failed: ${String(error)}`);
  }
}

export { createBriaaiModel, createModnetModel, createU2netpModel, rmbg };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map